/* =========================================================================
   ClueVine — achievements
   All achievements are derived purely from the existing stats state (plus the
   registered mode ids for "play them all"), so nothing extra needs tracking.
   ========================================================================= */

import type { IconName } from '../engine/types';
import { getModeStats, overall, type StatsState } from './statsLogic';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: IconName;
  check: (state: StatsState, modeIds: string[]) => boolean;
  progress?: (state: StatsState, modeIds: string[]) => { current: number; total: number };
}

function anyMode(state: StatsState, pred: (m: ReturnType<typeof getModeStats>) => boolean): boolean {
  return Object.values(state.modes).some(pred);
}

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first-sprout',
    name: 'First Sprout',
    description: 'Win your very first puzzle.',
    icon: 'leaf',
    check: (s) => overall(s).won >= 1,
  },
  {
    id: 'green-thumb',
    name: 'Green Thumb',
    description: 'Reach a 7-day daily streak.',
    icon: 'flame',
    check: (s) => overall(s).bestStreak >= 7,
    progress: (s) => ({ current: Math.min(7, overall(s).bestStreak), total: 7 }),
  },
  {
    id: 'evergreen',
    name: 'Evergreen',
    description: 'Reach a 30-day daily streak.',
    icon: 'flame',
    check: (s) => overall(s).bestStreak >= 30,
    progress: (s) => ({ current: Math.min(30, overall(s).bestStreak), total: 30 }),
  },
  {
    id: 'botanist',
    name: 'Botanist',
    description: 'Win at least once in every mode.',
    icon: 'sparkle',
    check: (s, ids) => ids.length > 0 && ids.every((id) => getModeStats(s, id).won >= 1),
    progress: (s, ids) => ({ current: ids.filter((id) => getModeStats(s, id).won >= 1).length, total: ids.length }),
  },
  {
    id: 'lucky-guess',
    name: 'Lucky Guess',
    description: 'Solve Cipher in a single guess, no hints.',
    icon: 'trophy',
    check: (s) => (getModeStats(s, 'cipher').guessDistribution[1] ?? 0) >= 1,
  },
  {
    id: 'wordsmith',
    name: 'Wordsmith',
    description: 'Win 50 puzzles.',
    icon: 'check',
    check: (s) => overall(s).won >= 50,
    progress: (s) => ({ current: Math.min(50, overall(s).won), total: 50 }),
  },
  {
    id: 'centurion',
    name: 'Centurion',
    description: 'Play 100 puzzles.',
    icon: 'chart',
    check: (s) => overall(s).played >= 100,
    progress: (s) => ({ current: Math.min(100, overall(s).played), total: 100 }),
  },
  {
    id: 'in-full-bloom',
    name: 'In Full Bloom',
    description: 'Score 2,000 or more in a single puzzle.',
    icon: 'trophy',
    check: (s) => anyMode(s, (m) => m.bestScore >= 2000),
  },
];

export function unlockedIds(state: StatsState, modeIds: string[], earned: Iterable<string> = []): Set<string> {
  const out = new Set(ACHIEVEMENTS.filter((a) => a.check(state, modeIds)).map((a) => a.id));
  // Earned achievements stay earned — e.g. Botanist doesn't relock when a new mode ships.
  for (const id of earned) if (ACHIEVEMENTS.some((a) => a.id === id)) out.add(id);
  return out;
}
